import React, { useEffect, useRef, useState } from 'react';
import { Participant, Track } from 'livekit-client';
import { AudioTrack, VideoTrack } from 'livekit-client';
import { Mic, MicOff, VideoOff, User } from 'lucide-react';
import { cn } from '../lib/utils';

interface ParticipantThumbnailProps {
    participant: Participant;
    isLocal?: boolean;
    isActive?: boolean;
    onClick?: () => void;
}

const ParticipantThumbnail: React.FC<ParticipantThumbnailProps> = ({
    participant,
    isLocal = false,
    isActive = false,
    onClick,
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [hasVideo, setHasVideo] = useState(false);
    const [hasAudio, setHasAudio] = useState(false);
    const [isMuted, setIsMuted] = useState(true);

    const [videoTrack, setVideoTrack] = useState<VideoTrack | null>(null);

    useEffect(() => {
        // Function to find and set the best video track
        const updateVideoTrack = () => {
            let foundTrack: VideoTrack | null = null;

            // Check for screen share first
            const screenSharePub = participant.getTrackPublication(Track.Source.ScreenShare);
            if (screenSharePub && screenSharePub.track && (isLocal || screenSharePub.isSubscribed)) {
                foundTrack = screenSharePub.track as VideoTrack;
            }
            // Then check for camera
            else {
                const cameraPub = participant.getTrackPublication(Track.Source.Camera);
                if (cameraPub && cameraPub.track && (isLocal || cameraPub.isSubscribed)) {
                    foundTrack = cameraPub.track as VideoTrack;
                }
            }

            // If we found a track, check if it's muted
            if (foundTrack) {
                if (foundTrack.isMuted) {
                    setHasVideo(false);
                } else {
                    setHasVideo(true);
                }
                setVideoTrack(foundTrack);
            } else {
                setHasVideo(false);
                setVideoTrack(null);
            }
        };

        // Initial check
        updateVideoTrack();

        // Helper to update mic state
        const updateMicState = () => {
            setIsMuted(!participant.isMicrophoneEnabled);
        };

        // Event handlers
        const handleTrackChange = () => {
            updateVideoTrack();
            updateMicState();
        };

        const handleTrackMute = (pub: any) => {
            if (pub.track && pub.track.kind === Track.Kind.Video) {
                setHasVideo(false);
            }
            updateMicState();
        };

        const handleTrackUnmute = (pub: any) => {
            if (pub.track && pub.track.kind === Track.Kind.Video) {
                setHasVideo(true);
            }
            updateMicState();
        };

        participant.on('trackSubscribed', handleTrackChange);
        participant.on('trackUnsubscribed', handleTrackChange);
        participant.on('localTrackPublished', handleTrackChange);
        participant.on('localTrackUnpublished', handleTrackChange);
        participant.on('trackMuted', handleTrackMute);
        participant.on('trackUnmuted', handleTrackUnmute);

        // Also listen for track published/unpublished for remote participants to catch audio tracks
        if (!isLocal) {
            participant.on('trackPublished', handleTrackChange);
            participant.on('trackUnpublished', handleTrackChange);
        }

        // Audio handling
        const handleAudioTrack = (track: Track) => {
            if (track.kind === Track.Kind.Audio && !isLocal) {
                const audioTrack = track as AudioTrack;
                if (audioRef.current) {
                    audioTrack.attach(audioRef.current);
                    setHasAudio(true);
                }
            }
        };

        const handleAudioDetach = (track: Track) => {
            if (track.kind === Track.Kind.Audio && !isLocal) {
                const audioTrack = track as AudioTrack;
                if (audioRef.current) {
                    audioTrack.detach(audioRef.current);
                    setHasAudio(false);
                }
            }
        };

        // Initial check
        updateMicState();

        // Initialize audio and set up listeners for remote participants
        if (!isLocal) {
            participant.audioTrackPublications.forEach((pub) => {
                if (pub.track && pub.isSubscribed) {
                    handleAudioTrack(pub.track);
                }
            });

            participant.on('trackSubscribed', handleAudioTrack);
            participant.on('trackUnsubscribed', handleAudioDetach);
        }

        return () => {
            participant.off('trackSubscribed', handleTrackChange);
            participant.off('trackUnsubscribed', handleTrackChange);
            participant.off('localTrackPublished', handleTrackChange);
            participant.off('localTrackUnpublished', handleTrackChange);
            participant.off('trackMuted', handleTrackMute);
            participant.off('trackUnmuted', handleTrackUnmute);

            if (!isLocal) {
                participant.off('trackSubscribed', handleAudioTrack);
                participant.off('trackUnsubscribed', handleAudioDetach);
            }
        };
    }, [participant, isLocal]);

    // Effect to attach video track when it changes
    useEffect(() => {
        const el = videoRef.current;
        if (videoTrack && el && hasVideo) {
            videoTrack.attach(el);
            return () => {
                videoTrack.detach(el);
            };
        }
    }, [videoTrack, hasVideo]);

    const displayName = participant.name || participant.identity;

    return (
        <div
            onClick={onClick}
            className={cn(
                'relative flex-shrink-0 rounded-lg overflow-hidden cursor-pointer transition-all duration-300',
                'bg-muted/30 dark:bg-muted/50',
                isActive
                    ? 'border-[3px] border-primary scale-110'
                    : 'border-2 border-border hover:border-primary/50 hover:scale-102',
                'w-[120px] h-[90px] md:w-[140px] md:h-[105px]'
            )}
        >
            {/* Video or Avatar */}
            {hasVideo ? (
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted={isLocal}
                    className="w-full h-full object-cover"
                    style={{ transform: isLocal ? 'scaleX(-1)' : 'none' }}
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                    <User className="w-8 h-8 text-muted-foreground" />
                </div>
            )}

            {/* Audio element for remote participants */}
            {!isLocal && <audio ref={audioRef} autoPlay />}

            {/* Name overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/60 to-transparent backdrop-blur-sm px-2 py-1.5">
                <p className="text-white text-xs font-medium truncate">
                    {isLocal ? 'You' : displayName}
                </p>
            </div>

            {/* Mute indicator */}
            {isMuted && (
                <div className="absolute top-1.5 right-1.5 bg-black/60 backdrop-blur-sm rounded-full p-1">
                    <MicOff className="w-3 h-3 text-white/90" strokeWidth={2.5} />
                </div>
            )}

            {/* Video off indicator */}
            {!hasVideo && (
                <div className="absolute top-1 left-1 bg-muted/90 rounded-full p-1">
                    <VideoOff className="w-3 h-3 text-muted-foreground" />
                </div>
            )}

            {/* Active indicator */}
            {isActive && (
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-primary/10" />
                </div>
            )}
        </div>
    );
};

export default ParticipantThumbnail;
